// src/pages/CartPage.jsx
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CartContext } from '../contexts/CartContext';
import Header from '../components/layout/Header';
import ImageService from '../services/ImageService';
import '../styles/cart.css';
import '../styles/cart-modal-animations.css';

// Fonction pour formater les prix avec séparateurs de milliers
const formatPrice = (price) => {
  return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const CartPage = () => {
  const navigate = useNavigate();
  const { 
    items, 
    quantities, 
    removeCompletelyFromCart, 
    updateQuantity,
    calculateTotal,
    getTotalItems,
    getRemainingAmount,
    isMinimumReached,
    // ✅ NOUVEAU : Fonctions pour les consignes
    hasConsigne,
    calculateConsigne,
    calculateGrandTotal,
    getNbCasiers,
    isVerreProduct,
    CONSIGNE_CASIER
  } = useContext(CartContext);

  // État pour gérer le modal
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  
  // Scroll automatique vers le haut au chargement de la page
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Rediriger vers la page d'accueil si le panier est vide après un délai
  useEffect(() => {
    if (items.length === 0) {
      const timer = setTimeout(() => {
        navigate('/welcome-page');
      }, 1500);
      
      return () => clearTimeout(timer);
    }
  }, [items.length, navigate]);

  const handleContinueShopping = () => {
    // Récupérer la dernière catégorie visitée
    const lastCategory = localStorage.getItem('lastVisitedCategory');
    console.log('Dernière catégorie visitée:', lastCategory);
    
    if (lastCategory) {
      // Si une catégorie a été visitée, y retourner directement
      navigate(`/products/${lastCategory}`);
    } else {
      // Sinon, aller à la catégorie par défaut (eaux)
      navigate('/products/eaux');
    }
  };

  const handleProceedToCheckout = () => {
    navigate('/delivery');
  };

  // Fonction pour ouvrir le modal avec le produit sélectionné
  const handleProductClick = (item) => {
    console.log('Produit cliqué:', item);
    setSelectedProduct(item);
    setIsModalOpen(true);
  };

  // Fonction pour fermer le modal
  const closeModal = () => {
    setIsModalOpen(false);
    // Attendre la fin de l'animation avant de réinitialiser le produit
    setTimeout(() => {
      setSelectedProduct(null);
    }, 300);
  };

  // Empêcher le scroll du body quand le modal est ouvert
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    // Cleanup
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isModalOpen]);

  if (items.length === 0) {
    return (
      <div className="cart-page">
        <Header 
          title="Panier" 
          showBackButton={true} 
          onBackClick={() => navigate('/welcome-page')}
        />
        <div className="empty-cart-container">
          <svg className="empty-cart-icon" viewBox="0 0 24 24" fill="currentColor">
            <path d="M9 20a1 1 0 11-2 0 1 1 0 012 0zm9 0a1 1 0 11-2 0 1 1 0 012 0zM3 3h2l.4 2M7 13h10l4-8H5.4L3 3" />
          </svg>
          <h2 className="empty-cart-title">Votre casier est vide</h2>
          <p className="empty-cart-message">
            Ajoutez des produits à votre casier pour passer commande
          </p>
          <button 
            onClick={() => navigate('/welcome-page')}
            className="empty-cart-button"
          >
            Découvrir nos produits
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <Header 
        title="Votre panier" 
        showBackButton={true} 
        onBackClick={handleContinueShopping}
      />
      
      <div className="cart-container">
        <h2 className="cart-section-title">Articles ({getTotalItems()})</h2>
        
        <div className="cart-items-list">
          {items.map((item, index) => (
            <div 
              key={item.id} 
              className="cart-item"
              style={{animationDelay: `${index * 0.05}s`}}
            >
              <div 
                className="cart-item-clickable"
                onClick={() => handleProductClick(item)}
                style={{ cursor: 'pointer' }}
              >
                <img 
                  src={item.displayImage || item.thumbnailImage} 
                  alt={item.displayName} 
                  className="cart-item-image"
                />
                
                <div className="cart-item-details">
                  <h3 className="cart-item-name">{item.displayName}</h3>
                  <span className="cart-item-price">{formatPrice(item.price)} F</span>
                  
                  {/* ✅ NOUVEAU : Badge discret pour les produits en verre (avec consigne) */}
                  {isVerreProduct && isVerreProduct(item) && (
                    <span className="cart-item-consigne-badge">
                      Casier consigné
                    </span>
                  )}
                </div>
              </div>
              
              <div className="cart-item-actions">
                <div className="cart-quantity-wrapper">
                  <div className="cart-quantity-controls">
                    <button 
                      onClick={() => updateQuantity(item.id, quantities[item.id] - 1)}
                      className="cart-quantity-btn decrement"
                      aria-label="Diminuer la quantité"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13H5v-2h14v2z" />
                      </svg>
                    </button>
                    
                    <span className="cart-quantity-value">
                      {quantities[item.id]}
                    </span>
                    
                    <button 
                      onClick={() => updateQuantity(item.id, quantities[item.id] + 1)}
                      className="cart-quantity-btn increment"
                      aria-label="Augmenter la quantité"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" />
                      </svg>
                    </button>
                  </div>
                  
                  <div className="cart-item-subtotal">
                    {formatPrice(item.price * quantities[item.id])} F
                  </div>
                </div>
                
                <button 
                  onClick={() => removeCompletelyFromCart(item.id)}
                  className="cart-item-remove"
                  aria-label="Supprimer l'article"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Résumé du panier et boutons d'action */}
      <div className="cart-summary">
        {/* ✅ MODIFIÉ : Affichage du sous-total produits */}
        <div className="cart-total cart-subtotal-line">
          <span className="cart-total-label">Sous-total ({getTotalItems()} articles)</span>
          <span className="cart-total-value">{formatPrice(calculateTotal())} F</span>
        </div>
        
        {/* ✅ NOUVEAU : Section consigne casiers (affichée seulement si hasConsigne) */}
        {hasConsigne && hasConsigne() && (
          <div className="cart-consigne-section">
            <div className="cart-consigne-line">
              <div className="cart-consigne-info">
                <span className="cart-consigne-label">
                  Consigne casiers ({getNbCasiers()} × {formatPrice(CONSIGNE_CASIER)} F)
                </span>
                <span className="cart-consigne-hint">
                  Remboursable au retour des casiers
                </span>
              </div>
              <span className="cart-consigne-value">{formatPrice(calculateConsigne())} F</span>
            </div>
          </div>
        )}
        
        {/* ✅ MODIFIÉ : Ligne du total à payer (produits + consigne si applicable) */}
        <div className="cart-total cart-grand-total-line">
          <span className="cart-total-label cart-grand-total-label">Total à payer</span>
          <span className="cart-total-value cart-grand-total-value">
            {formatPrice(calculateGrandTotal ? calculateGrandTotal() : calculateTotal())} F
          </span>
        </div>
        
        {!isMinimumReached() && (
          <div className="minimum-order-warning-cart">
            <svg className="warning-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
            </svg>
            <span className="warning-text">
              Il manque <strong>{formatPrice(getRemainingAmount())} F</strong> pour atteindre le montant minimal de commande (10 000 F)
            </span>
          </div>
        )}
        
        <div className="cart-actions">
          <button 
            onClick={handleContinueShopping}
            className="cart-action-button secondary"
          >
            Continuer achats
          </button>
          
          <button 
            onClick={handleProceedToCheckout}
            className={`cart-action-button primary ${!isMinimumReached() ? 'disabled' : ''}`}
            disabled={!isMinimumReached()}
          >
            Commander
            <svg className="cart-action-icon" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8-8-8z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal détail produit */}
      {selectedProduct && (
        <div 
          className={`product-modal-overlay ${isModalOpen ? 'active' : ''}`}
          onClick={closeModal}
        >
          <div 
            className={`product-modal-dual ${isModalOpen ? 'active' : ''}`}
            onClick={(e) => e.stopPropagation()}
          >
            <button 
              className="modal-close-btn"
              onClick={closeModal}
              aria-label="Fermer"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" />
              </svg>
            </button>

            <div className="modal-content-dual">
              {/* Vérifier si c'est un produit en unité (bidon) */}
              {ImageService.isUnitProduct(selectedProduct) ? (
                // Pour les bidons : afficher seulement l'image du bidon (centrée)
                <div className="modal-single-image">
                  <div className="modal-image-main modal-bottle-animate">
                    <img 
                      src={ImageService.getBottleImageUrl(
                        selectedProduct.code_produit || 
                        selectedProduct.code || 
                        ''
                      )}
                      alt={selectedProduct.displayName}
                      className="modal-product-image-large"
                      onError={(e) => {
                        e.target.src = selectedProduct.displayImage || 
                                      selectedProduct.thumbnailImage || 
                                      ImageService.getPlaceholderUrl();
                      }}
                    />
                  </div>
                </div>
              ) : (
                // Pour les packs : afficher bouteille + pack
                <div className="modal-images-container">
                  {/* Colonne gauche : Image de la bouteille (GRANDE) */}
                  <div className="modal-image-main modal-bottle-animate">
                    <img 
                      src={ImageService.getBottleImageUrl(
                        selectedProduct.code_produit || 
                        selectedProduct.code || 
                        ''
                      )}
                      alt={`${selectedProduct.displayName} - Bouteille`}
                      className="modal-product-image-large"
                      onError={(e) => {
                        e.target.src = selectedProduct.displayImage || 
                                      selectedProduct.thumbnailImage || 
                                      ImageService.getPlaceholderUrl();
                      }}
                    />
                  </div>

                  {/* Colonne droite : Image du pack (PETITE) */}
                  <div className="modal-image-secondary modal-pack-animate">
                    <img 
                      src={ImageService.getPackImageUrl(
                        selectedProduct.code_produit || 
                        selectedProduct.code || 
                        ''
                      )}
                      alt={`${selectedProduct.displayName} - Pack`}
                      className="modal-product-image-small"
                      onError={(e) => {
                        e.target.src = selectedProduct.displayImage || 
                                      selectedProduct.thumbnailImage || 
                                      ImageService.getPlaceholderUrl();
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Bande d'informations en bas */}
              <div className="modal-info-band">
                <h2 className="modal-product-name">{selectedProduct.displayName}</h2>
                
                {/* ✅ NOUVEAU : Badge consigne dans la modal */}
                {isVerreProduct && isVerreProduct(selectedProduct) && (
                  <div className="modal-consigne-badge">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                    </svg>
                    Casier consigné ({formatPrice(CONSIGNE_CASIER)} F remboursable)
                  </div>
                )}
                
                <p className="modal-product-packaging">
                  {selectedProduct.packaging || 
                   `${ImageService.isUnitProduct(selectedProduct) 
                      ? 'Vendu à l\'unité' 
                      : `Pack de ${selectedProduct.bouteilles_par_pack || '6'} bouteilles`}`}
                </p>
                <div className="modal-product-price">
                  {formatPrice(selectedProduct.price)} F {ImageService.isUnitProduct(selectedProduct) ? '' : '/ pack'}
                </div>
                
                <div className="modal-description-section">
                  <h3 className="modal-description-title">Description</h3>
                  <p className="modal-description-text">
                    {selectedProduct.description || 
                     `${selectedProduct.displayName} est une boisson de qualité supérieure, 
                     parfaite pour se rafraîchir à tout moment de la journée. 
                     ${ImageService.isUnitProduct(selectedProduct) 
                        ? 'Vendu à l\'unité pour votre commodité.' 
                        : `Vendue en pack de ${selectedProduct.bouteilles_par_pack || '6'} bouteilles.`}
                     Elle offre une expérience de dégustation unique grâce à sa fraîcheur 
                     et son goût authentique.`}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CartPage;